// PRIMEIRO, antes de qualquer outro módulo ler o armazenamento — ver o arquivo.
import './demo/isolar';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { MODO_DEMO } from './demo/modo';
import FaixaDaDemo from './demo/FaixaDaDemo';
import SeloDoHml, { EH_HML } from './components/SeloDoHml';
import { AuthProvider } from './contexts/AuthContext';
import { capturarAviso } from './utils/reciboDoAviso';
import { capturarAbrir } from './utils/abrirDoLink';
import { rotaPublica, rotaDeConsentimento } from './utils/rotaPublica';
import { PaginaDeConvite, PaginaDeSaida } from './components/ConvitePublico';
import PaginaDeConsentimento from './components/PaginaDeConsentimento';
import ConviteDeMeta from './components/ConviteDeMeta';
import {
  conviteDeMetaPendente, guardarConviteDeMeta, rotaDeConviteDeMeta,
} from './utils/conviteDeMeta';

/*
 * `sendDefaultPii: false` — explícito, e não omitido.
 *
 * Estava `true`, o que faz o Sentry receber o endereço de IP junto com cada
 * erro. Não era necessário para consertar nada: o que resolve um erro é a
 * pilha, a mensagem e a versão do navegador, e nada disso é dado pessoal.
 *
 * A tela de login promete "Seus dados ficam seguros e acessíveis apenas por
 * você", e mandar o IP para um terceiro sem precisar é a promessa perdendo um
 * pedaço em silêncio. Foi escrever a política de privacidade que trouxe isso à
 * tona — ter que declarar o que sai obriga a olhar o que sai.
 *
 * `false` escrito e não a opção apagada: o padrão do SDK já é `false`, mas
 * apagar deixaria a próxima pessoa livre para pôr `true` sem saber que já foi
 * decidido. `privacidade.test.ts` prende esta linha à frase da política.
 */
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: EH_HML ? 'hml' : 'production',
  sendDefaultPii: false,
});

/*
 * Antes de React montar, e muito antes de qualquer redirecionamento.
 *
 * O login do Google volta para `window.location.origin` — só o domínio, sem a
 * query string. Se o `?aviso=` da notificação não for guardado AQUI, ele morre
 * no meio do login de quem estava deslogada, e o clique dela é contado como
 * "ignorou".
 */
capturarAviso();
// O `?abrir=` do email (newsletter), pela mesma razão: o login não devolve a query.
capturarAbrir();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

/*
 * O desvio das telas públicas — antes do `AuthProvider`, e o "antes" é o ponto.
 *
 * `App.tsx` começa com `if (!session) return <LoginPage />`. Quem clica num
 * convite quase nunca tem conta: passando por ali, veria uma tela de login e
 * nunca o convite. Pior — a única forma de RECUSAR seria criar um cadastro.
 *
 * Fora do provedor também porque estas telas não devem nem tentar buscar
 * sessão: elas falam com o servidor por `fetch` puro, e o que autoriza é o
 * token da URL.
 *
 * A Vercel entrega `index.html` para qualquer caminho (`vercel.json`), então
 * `/convite/<token>` chega aqui com a URL intacta.
 */
const publica = rotaPublica(window.location.pathname);

/*
 * A tela de consentimento do conector de IA fica DENTRO do `AuthProvider` —
 * ela precisa de sessão para decidir — e fora do `App`, que mostraria o painel
 * inteiro para quem veio do Claude responder uma pergunta só.
 */
const consentimento = rotaDeConsentimento(window.location.pathname);

/*
 * O convite de meta — o único fluxo que atravessa o LOGIN.
 *
 * Ele desvia antes do `App` como os outros (quem chega quase nunca tem conta,
 * e cairia na tela de login sem nunca ver o convite), mas fica DENTRO do
 * `AuthProvider`: ver a meta não custa nada, entrar nela precisa de sessão.
 *
 * ── Os dois lados do `??`, e por que o segundo existe ───────────────────
 *
 * `signInWithOAuth` volta para `window.location.origin` — só o domínio. O
 * caminho `/convite-meta/<token>` NÃO volta do Google, e sem o segundo ramo
 * todo convidado novo — que é o caso normal aqui — cairia no painel vazio sem
 * nunca ter entrado, e sem nada explicando o que aconteceu.
 *
 * Guardar acontece no carregamento do módulo, antes de React montar e muito
 * antes de qualquer redirecionamento. É a mesma mecânica de `capturarAviso`,
 * pela mesma razão.
 */
const conviteDeMetaDaUrl = rotaDeConviteDeMeta(window.location.pathname);
if (conviteDeMetaDaUrl) guardarConviteDeMeta(conviteDeMetaDaUrl);
const conviteDeMeta = conviteDeMetaDaUrl ?? conviteDeMetaPendente();

root.render(
  <React.StrictMode>
    {EH_HML && <SeloDoHml />}
    {publica?.tipo === 'convite' ? <PaginaDeConvite token={publica.token} />
      : publica?.tipo === 'sair' ? <PaginaDeSaida token={publica.token} />
      : conviteDeMeta ? (
        <AuthProvider>
          <ConviteDeMeta token={conviteDeMeta} />
        </AuthProvider>
      )
      : consentimento ? (
        <AuthProvider>
          <PaginaDeConsentimento />
        </AuthProvider>
      )
      : (
        <AuthProvider>
          {/* A faixa fixa e o aviso de "nada é gravado" — só em `/demo`. */}
          {MODO_DEMO && <FaixaDaDemo />}
          <App />
        </AuthProvider>
      )}
  </React.StrictMode>
);
